import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import useThemeContext from '../../../../contexts/theme';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import { useQuery } from '../../../../hooks/useQuery';
import ConsultsService from '../../../../services/ConsultsService';
import { CustomStyle } from '../../../CustomSelectStyle';
import { CustomStyle as CustomStyleDarkTheme } from '../../../CustomSelectStyleDarkTheme';
import FilterRadioButton from '../../../FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../../FilterRadioButtonsContainer';
import MyModal from '../../../Modal';
import NoData from '../../../NoData';
import { SecondaryButton } from '../../../SecondaryButton';
import AbateResultModal from './components/AbateResultModal';
import AddInstructionModal from './components/AddInstructionModal';
import CopyModal from './components/CopyModal';
import DeleteAllByDirectionModal from './components/DeleteAllByDirectionModal';
import DetailedInstructions from './components/DetailedInstructinos';
import EditInstructionModalShow from './components/EditInstructionModalShow';
import RemoveInstructionModal from './components/RemoveInstructionModal';
import { ManualRoutingModalBody, SimpleRoutingVtItensContainer } from './styles';
import { IManualRoutingGetResponseApi, ListaCidRegiao, ListaItensVt, ListaTrajetosCopum, Rotas, TrajetoInstruc } from './types';

interface IManualRoutingModal {
  manualRoutingModalShow: boolean,
  setManualRoutingModalShow: Dispatch<SetStateAction<boolean>>,
  getResult: () => Promise<void>,
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isMultiRoute: boolean;
}

export default function ManualRoutingModal({
  manualRoutingModalShow,
  setManualRoutingModalShow,
  getResult,
  setIsLoading,
  isMultiRoute,
}: IManualRoutingModal) {
  const [manualRoutingType, setManualRoutingType] = useState<'simple' | 'detailed'>('simple');
  const [instructions, setInstructions] = useState<TrajetoInstruc[]>([]);
  const [direction, setDirection] = useState<'going' | 'returning'>('going');
  const [vtItensOptions, setVtItensOptions] = useState<ListaItensVt[]>([]);
  const [regionsOptions, setRegionsOptions] = useState<ListaCidRegiao[]>([]);
  const [routesOptions, setRoutesOptions] = useState<Rotas[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<Rotas>({} as Rotas);

  const [addInstructionModalShow, setAddInstructionModalShow] = useState(false);
  const [removeInstructionModalShow, setRemoveInstructionModalShow] = useState(false);
  const [instructionToBeRemoved, setInstructionToBeRemoved] = useState({} as TrajetoInstruc);
  const [editInstructionModalShow, setEditInstructionModalShow] = useState(false);
  const [instructionBeingEditted, setInstructionBeingEditted] = useState({} as TrajetoInstruc);
  const [abateResultModalShow, setAbateResultModalShow] = useState(false);
  const [copyModalShow, setCopyModalShow] = useState(false);
  const [copyOptions, setCopyOptioins] = useState<ListaTrajetosCopum[]>([]);
  const [deleteAllByDirectionModalShow, setDeleteAllByDirectionModalShow] = useState(false);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, userName } = useGetGeneralAppData();
  const query = useQuery();
  const companyCode = query.get('codEmpresa');
  const { consultCode } = useParams();
  const { selectedTheme } = useThemeContext();

  const canGenerateRouting = useMemo(() => {
    const allGoingInstructions = instructions?.filter((instruction) => instruction.codSentido === 1);
    const allReturningInstructions = instructions?.filter((instruction) => instruction.codSentido === 2);
    return instructions && instructions?.length && allGoingInstructions && allGoingInstructions?.length && allReturningInstructions && allReturningInstructions?.length;
  }, [instructions]);

  const getDataForManualRouting = useCallback(async () => {
    await apiCall({
      apiToCall: ConsultsService.getDataToMakeManualRouting,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        consultCode,
        codEmpresa: companyCode ?? 0,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível abrir o quadro de Roteirização Manual. Por favor, tente novamente',
      catchAction: () => setManualRoutingModalShow(false),
      actionAfterResponse(response: IManualRoutingGetResponseApi) {
        if (!response.codigo) {
          toast.error(`Não foi possível abrir o quadro de Roteirização Manual. Por favor, tente novamente (${response.msg})`);
          setManualRoutingModalShow(false);
          return;
        }

        setVtItensOptions(response.listaItensVT ?? []);
        setRegionsOptions(response.listaCidRegiao ?? []);
        setInstructions(response.trajetoInstrucoes ?? []);
        setCopyOptioins(response.listaTrajetosCopia ?? []);
        setRoutesOptions(response.rotas ?? []);
        if (response.rotas && response.rotas.length > 0) {
          setSelectedRoute(response.rotas[0]);
        }
      },
    });
  }, [apiCall, companyCode, consultCode, setIsLoading, setManualRoutingModalShow, userCode, userProfile]);

  const instructionsSeparatedByDirection = useMemo(() => {
    const goingInstructions = instructions?.filter((instruction) => instruction.codSentido === 1);
    const returningInstruction = instructions?.filter((instruction) => instruction.codSentido === 2);

    return {
      going: goingInstructions ?? [],
      returning: returningInstruction ?? [],
    };
  }, [instructions]);

  const instructionsSeparatedByRoute = useMemo(() => (
    instructions?.filter((instruction) => instruction.rota === selectedRoute.rota)
  ), [instructions, selectedRoute.rota]);

  const kindOfManualRoutingAlreadyCreated = useMemo(() => {
    if (!instructions || instructions?.length === 0) {
      return undefined;
    }

    const hasSomeInstructionMissingInformation = instructions.some((instruction) => (
      instruction.destino === 'Local indefinido' || instruction.origem === 'Local indefinido'
    ));

    const kindOfManualRouting = hasSomeInstructionMissingInformation ? 'simple' : 'detailed';
    setManualRoutingType(kindOfManualRouting);
    return kindOfManualRouting;
  }, [instructions]);

  const isInstructionAbate = useMemo(() => (
    instructionsSeparatedByDirection[direction]?.some((x) => x?.codItem === 1758)
  ), [direction, instructionsSeparatedByDirection]);

  function handleOpenDeleteInstructionModal(instruction: TrajetoInstruc) {
    setInstructionToBeRemoved(instruction);
    setRemoveInstructionModalShow(true);
  }

  function handleOpenEditInstructionModal(instruction: TrajetoInstruc) {
    setInstructionBeingEditted(instruction);
    setEditInstructionModalShow(true);
  }

  const generateManualRouting = useCallback(async () => {
    toast.dismiss('lastReclamationDetails');
    await apiCall({
      apiToCall: ConsultsService.generateManualRouting,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: companyCode,
        codConsulta: consultCode,
        tipoAcao: 0,
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível gerar a roteirização. Por favor, tente novamente',
      actionAfterResponse: async (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível gerar a roteirização manual. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setManualRoutingModalShow(false);
        await getResult();
      }
    });
  }, [apiCall, companyCode, consultCode, getResult, setIsLoading, setManualRoutingModalShow, userCode, userName, userProfile]);

  useEffect(() => {
    if (!manualRoutingModalShow) {
      return;
    }
    getDataForManualRouting();
  }, [getDataForManualRouting, manualRoutingModalShow]);

  return (
    <>
      <MyModal
        show={manualRoutingModalShow}
        title="Realizar roteirização manual"
        closeButtonLabel="Fechar"
        actionButtonLabel="Gerar Roteirização"
        onClose={() => {setManualRoutingModalShow(false); toast.dismiss('lastReclamationDetails');}}
        onAction={() => generateManualRouting()}
        isActionButtonDisabled={!canGenerateRouting}
        type="action"
        fullscreen
        size='xl'
        modalBody={(
          <ManualRoutingModalBody>
            <FilterRadioButtonsContainer>
              <FilterRadioButton
                selected={manualRoutingType === 'simple'}
                onClick={() => setManualRoutingType('simple')}
                disabled={kindOfManualRoutingAlreadyCreated && kindOfManualRoutingAlreadyCreated === 'detailed'}
              >
                Simplificada
              </FilterRadioButton>
              <FilterRadioButton
                selected={manualRoutingType === 'detailed'}
                onClick={() => setManualRoutingType('detailed')}
                disabled={kindOfManualRoutingAlreadyCreated && kindOfManualRoutingAlreadyCreated === 'simple'}
              >
                Detalhada
              </FilterRadioButton>
            </FilterRadioButtonsContainer>

            {manualRoutingType === 'simple' && (
              <SimpleRoutingVtItensContainer>

                {isMultiRoute ? (
                  <div style={{ width: '400px'}}>
                    <Select
                      value={{ value: selectedRoute.rota, label: selectedRoute.rota ? `Trecho #${selectedRoute.rota} - ${selectedRoute.origem} -> ${selectedRoute.destino} (${selectedRoute.tipoRotaMRDescr})` : 'Selecione um trecho' }}
                      options={routesOptions.map((route) => ({ value: route.rota, label: `Trecho #${route.rota} - ${route.origem} -> ${route.destino} (${route.tipoRotaMRDescr})` })) ?? []}
                      onChange={(route) => setSelectedRoute(routesOptions.find((x) => x.rota === route?.value) ?? {} as Rotas)}
                      placeholder={'Selecione o trecho'}
                      styles={selectedTheme === 'dark' ? CustomStyleDarkTheme : CustomStyle}
                      classNamePrefix="react-select"
                      className="react-select-container"
                    />
                  </div>
                ) : (
                  <FilterRadioButtonsContainer>
                    <FilterRadioButton selected={direction === 'going'} onClick={() => setDirection('going')}>
                    Ida
                    </FilterRadioButton>
                    <FilterRadioButton selected={direction === 'returning'} onClick={() => setDirection('returning')}>
                    Volta
                    </FilterRadioButton>
                  </FilterRadioButtonsContainer>

                )}

                {direction === 'going' && !isMultiRoute && (
                  <>
                    <FilterRadioButtonsContainer>
                      <SecondaryButton
                        onClick={() => setAddInstructionModalShow(true)}
                        disabled={isInstructionAbate && instructionsSeparatedByDirection.going && instructionsSeparatedByDirection.going.length > 0}
                      >
                        Adicionar bilhete #{(instructionsSeparatedByDirection.going?.length + 1) || 1} Ida
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setAbateResultModalShow(true)}
                        disabled={!isInstructionAbate && instructionsSeparatedByDirection.going && instructionsSeparatedByDirection.going.length > 0}
                      >
                        Abater Resultado (Ida)
                      </SecondaryButton>
                      {!isMultiRoute && (
                        <SecondaryButton
                          onClick={() => setCopyModalShow(true)}
                          disabled={copyOptions.length === 0 && !(instructionsSeparatedByDirection.returning?.length)}
                        >
                        Copiar para Ida
                        </SecondaryButton>
                      )}
                      <SecondaryButton
                        onClick={() => setDeleteAllByDirectionModalShow(true)}
                        disabled={!instructionsSeparatedByDirection.going || instructionsSeparatedByDirection.going.length === 0}
                      >
                        Remover todas as instruções (Ida)
                      </SecondaryButton>
                    </FilterRadioButtonsContainer>
                    {(instructionsSeparatedByDirection.going?.length === 0 || !instructionsSeparatedByDirection.going) && (
                      <NoData
                        icon="emptyBox"
                        label={(
                          <>
                          Começe a montar a roteirização manual simplificada (sentido Ida) escolhendo o primeiro bilhete.
                          </>
                        )}
                      />
                    )}

                    {(instructionsSeparatedByDirection.going?.length > 0) && (
                      <DetailedInstructions
                        manualRoutingType={manualRoutingType}
                        instructions={instructionsSeparatedByDirection.going}
                        vtItensOptions={vtItensOptions}
                        handleOpenDeleteModal={handleOpenDeleteInstructionModal}
                        handleOpenEditModal={handleOpenEditInstructionModal}
                      />
                    )}
                  </>
                )}

                {direction === 'returning' && !isMultiRoute && (
                  <>
                    <FilterRadioButtonsContainer>
                      <SecondaryButton
                        onClick={() => setAddInstructionModalShow(true)}
                        disabled={isInstructionAbate && instructionsSeparatedByDirection.returning && instructionsSeparatedByDirection.returning.length > 0}
                      >
                        Adicionar bilhete #{(instructionsSeparatedByDirection.returning?.length + 1) || 1} Volta
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setAbateResultModalShow(true)}
                        disabled={!isInstructionAbate && instructionsSeparatedByDirection.returning && instructionsSeparatedByDirection.returning.length > 0}
                      >
                        Abater Resultado (Volta)
                      </SecondaryButton>
                      {!isMultiRoute && (
                        <SecondaryButton
                          onClick={() => setCopyModalShow(true)}
                          disabled={copyOptions.length === 0 && !(instructionsSeparatedByDirection.going?.length)}
                        >
                          Copiar para Volta
                        </SecondaryButton>
                      )}
                      <SecondaryButton
                        onClick={() => setDeleteAllByDirectionModalShow(true)}
                        disabled={!instructionsSeparatedByDirection.returning || instructionsSeparatedByDirection.returning.length === 0}
                      >
                        Remover todas as instruções (Volta)
                      </SecondaryButton>
                    </FilterRadioButtonsContainer>
                    {(instructionsSeparatedByDirection.returning?.length === 0 || !instructionsSeparatedByDirection.returning) && (
                      <NoData
                        icon="emptyBox"
                        label={(
                          <>
                          Começe a montar a roteirização manual simplificada (sentido Volta) escolhendo o primeiro bilhete.
                          </>
                        )}
                      />
                    )}

                    {(instructionsSeparatedByDirection.returning?.length > 0) && (
                      <DetailedInstructions
                        manualRoutingType={manualRoutingType}
                        instructions={instructionsSeparatedByDirection.returning}
                        vtItensOptions={vtItensOptions}
                        handleOpenDeleteModal={handleOpenDeleteInstructionModal}
                        handleOpenEditModal={handleOpenEditInstructionModal}
                      />
                    )}
                  </>
                )}

                {isMultiRoute && (
                  <>
                    <FilterRadioButtonsContainer>
                      <SecondaryButton
                        onClick={() => setAddInstructionModalShow(true)}
                        disabled={isInstructionAbate && instructionsSeparatedByRoute && instructionsSeparatedByRoute.length > 0}
                      >
                        Adicionar bilhete #{(instructionsSeparatedByRoute?.length + 1) || 1} Trecho {selectedRoute.rota}
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setAbateResultModalShow(true)}
                        disabled={!isInstructionAbate && instructionsSeparatedByRoute && instructionsSeparatedByRoute.length > 0}
                      >
                        Abater Resultado (Trecho {selectedRoute.rota})
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setDeleteAllByDirectionModalShow(true)}
                        disabled={!instructionsSeparatedByRoute || instructionsSeparatedByRoute.length === 0}
                      >
                        Remover todas as instruções (Trecho {selectedRoute.rota})
                      </SecondaryButton>
                    </FilterRadioButtonsContainer>
                    {(instructionsSeparatedByRoute?.length === 0 || !instructionsSeparatedByRoute) && (
                      <NoData
                        icon="emptyBox"
                        label={(
                          <>
                          Começe a montar a roteirização manual simplificada (Trecho {selectedRoute.rota}) escolhendo o primeiro bilhete.
                          </>
                        )}
                      />
                    )}

                    {(instructionsSeparatedByRoute?.length > 0) && (
                      <DetailedInstructions
                        manualRoutingType={manualRoutingType}
                        instructions={instructionsSeparatedByRoute}
                        vtItensOptions={vtItensOptions}
                        handleOpenDeleteModal={handleOpenDeleteInstructionModal}
                        handleOpenEditModal={handleOpenEditInstructionModal}
                      />
                    )}
                  </>
                )}
              </SimpleRoutingVtItensContainer>
            )}

            {manualRoutingType === 'detailed' && (
              <SimpleRoutingVtItensContainer>

                {isMultiRoute ? (
                  <div style={{ width: '400px'}}>
                    <Select
                      value={{ value: selectedRoute.rota, label: selectedRoute.rota ? `Trecho #${selectedRoute.rota} - ${selectedRoute.origem} -> ${selectedRoute.destino} (${selectedRoute.tipoRotaMRDescr})` : 'Selecione um trecho' }}
                      options={routesOptions.map((route) => ({ value: route.rota, label: `Trecho #${route.rota} - ${route.origem} -> ${route.destino} (${route.tipoRotaMRDescr})` })) ?? []}
                      onChange={(route) => setSelectedRoute(routesOptions.find((x) => x.rota === route?.value) ?? {} as Rotas)}
                      placeholder={'Selecione o trecho'}
                      styles={selectedTheme === 'dark' ? CustomStyleDarkTheme : CustomStyle}
                      classNamePrefix="react-select"
                      className="react-select-container"
                    />
                  </div>
                ) : (
                  <FilterRadioButtonsContainer>
                    <FilterRadioButton selected={direction === 'going'} onClick={() => setDirection('going')}>
                    Ida
                    </FilterRadioButton>
                    <FilterRadioButton selected={direction === 'returning'} onClick={() => setDirection('returning')}>
                    Volta
                    </FilterRadioButton>
                  </FilterRadioButtonsContainer>

                )}

                {direction === 'going' && !isMultiRoute && (
                  <>
                    <FilterRadioButtonsContainer>
                      <SecondaryButton
                        onClick={() => setAddInstructionModalShow(true)}
                        disabled={isInstructionAbate && instructionsSeparatedByDirection.going && instructionsSeparatedByDirection.going.length > 0}
                      >
                      Adicionar Instrução #{(instructionsSeparatedByDirection.going?.length + 1) || 1} Ida
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setAbateResultModalShow(true)}
                        disabled={!isInstructionAbate && instructionsSeparatedByDirection.going && instructionsSeparatedByDirection.going.length > 0}
                      >
                      Abater Resultado (Ida)
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setCopyModalShow(true)}
                        disabled={copyOptions.length === 0}
                      >
                      Copiar para Ida
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setDeleteAllByDirectionModalShow(true)}
                        disabled={!instructionsSeparatedByDirection.going || instructionsSeparatedByDirection.going.length === 0}
                      >
                      Remover todas as instruções (Ida)
                      </SecondaryButton>
                    </FilterRadioButtonsContainer>
                    {(instructionsSeparatedByDirection.going?.length === 0 || !instructionsSeparatedByDirection.going) && (
                      <NoData
                        icon="emptyBox"
                        label={(
                          <>
                            Começe a montar a roteirização manual detalhada (sentido Ida) escolhendo o primeiro bilhete.
                          </>
                        )}
                      />
                    )}

                    {(instructionsSeparatedByDirection.going?.length > 0) && (
                      <DetailedInstructions
                        manualRoutingType={manualRoutingType}
                        instructions={instructionsSeparatedByDirection.going}
                        vtItensOptions={vtItensOptions}
                        handleOpenDeleteModal={handleOpenDeleteInstructionModal}
                        handleOpenEditModal={handleOpenEditInstructionModal}
                      />
                    )}
                  </>
                )}

                {direction === 'returning' && !isMultiRoute && (
                  <>
                    <FilterRadioButtonsContainer>
                      <SecondaryButton
                        onClick={() => setAddInstructionModalShow(true)}
                        disabled={isInstructionAbate && instructionsSeparatedByDirection.returning && instructionsSeparatedByDirection.returning.length > 0}
                      >
                      Adicionar Instrução #{(instructionsSeparatedByDirection.returning?.length + 1) || 1} Volta
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setAbateResultModalShow(true)}
                        disabled={!isInstructionAbate && instructionsSeparatedByDirection.returning && instructionsSeparatedByDirection.returning.length > 0}
                      >
                      Abater Resultado (Volta)
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setCopyModalShow(true)}
                        disabled={copyOptions.length === 0}
                      >
                      Copiar para Volta
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setDeleteAllByDirectionModalShow(true)}
                        disabled={!instructionsSeparatedByDirection.returning || instructionsSeparatedByDirection.returning.length === 0}
                      >
                      Remover todas as instruções (Volta)
                      </SecondaryButton>
                    </FilterRadioButtonsContainer>
                    {(instructionsSeparatedByDirection.returning?.length === 0 || !instructionsSeparatedByDirection.returning) && (
                      <NoData
                        icon="emptyBox"
                        label={(
                          <>
                            Começe a montar a roteirização manual detalhada (sentido Volta) escolhendo o primeiro bilhete.
                          </>
                        )}
                      />
                    )}

                    {(instructionsSeparatedByDirection.returning?.length > 0) && (
                      <DetailedInstructions
                        manualRoutingType={manualRoutingType}
                        instructions={instructionsSeparatedByDirection.returning}
                        vtItensOptions={vtItensOptions}
                        handleOpenDeleteModal={handleOpenDeleteInstructionModal}
                        handleOpenEditModal={handleOpenEditInstructionModal}
                      />
                    )}
                  </>
                )}

                {isMultiRoute && (
                  <>
                    <FilterRadioButtonsContainer>
                      <SecondaryButton
                        onClick={() => setAddInstructionModalShow(true)}
                        disabled={isInstructionAbate && instructionsSeparatedByRoute && instructionsSeparatedByRoute.length > 0}
                      >
                      Adicionar Instrução #{(instructionsSeparatedByRoute?.length + 1) || 1} Trecho {selectedRoute.rota}
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setAbateResultModalShow(true)}
                        disabled={!isInstructionAbate && instructionsSeparatedByRoute && instructionsSeparatedByRoute.length > 0}
                      >
                      Abater Resultado (Trecho {selectedRoute.rota})
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setDeleteAllByDirectionModalShow(true)}
                        disabled={!instructionsSeparatedByRoute || instructionsSeparatedByRoute.length === 0}
                      >
                      Remover todas as instruções (Trecho {selectedRoute.rota})
                      </SecondaryButton>
                    </FilterRadioButtonsContainer>
                    {(instructionsSeparatedByRoute?.length === 0 || !instructionsSeparatedByRoute) && (
                      <NoData
                        icon="emptyBox"
                        label={(
                          <>
                            Começe a montar a roteirização manual detalhada (Trecho {selectedRoute.rota}) escolhendo o primeiro bilhete.
                          </>
                        )}
                      />
                    )}

                    {(instructionsSeparatedByRoute?.length > 0) && (
                      <DetailedInstructions
                        manualRoutingType={manualRoutingType}
                        instructions={instructionsSeparatedByRoute}
                        vtItensOptions={vtItensOptions}
                        handleOpenDeleteModal={handleOpenDeleteInstructionModal}
                        handleOpenEditModal={handleOpenEditInstructionModal}
                      />
                    )}
                  </>
                )}
              </SimpleRoutingVtItensContainer>
            )}
          </ManualRoutingModalBody>
        )}
      />

      <AddInstructionModal
        addInstructionModalShow={addInstructionModalShow}
        setAddInstructionModalShow={setAddInstructionModalShow}
        currentInstructions={instructions}
        vtItensOptions={vtItensOptions}
        regionsOptions={regionsOptions}
        direction={direction}
        instructionsSeparatedByDirection={instructionsSeparatedByDirection}
        selectedRoute={selectedRoute}
        instructionsSeparatedByRoute={instructionsSeparatedByRoute}
        setInstructions={setInstructions}
        setIsLoading={setIsLoading}
        manualRoutingType={manualRoutingType}
        isMultiRoute={isMultiRoute}
      />

      <RemoveInstructionModal
        instructionToBeRemoved={instructionToBeRemoved}
        removeInstructionModalShow={removeInstructionModalShow}
        setRemoveInstructionModalShow={setRemoveInstructionModalShow}
        vtItensOptions={vtItensOptions}
        setIsLoading={setIsLoading}
        setInstructions={setInstructions}
      />

      <EditInstructionModalShow
        editInstructionModalShow={editInstructionModalShow}
        setEditInstructionModalShow={setEditInstructionModalShow}
        instructionBeingEditted={instructionBeingEditted}
        manualRoutingType={manualRoutingType}
        vtItensOptions={vtItensOptions}
        regionsOptions={regionsOptions}
        setInstructions={setInstructions}
        setIsLoading={setIsLoading}
      />

      <AbateResultModal
        abateResultModalShow={abateResultModalShow}
        setAbateResultModalShow={setAbateResultModalShow}
        setIsLoading={setIsLoading}
        setInstructions={setInstructions}
        direction={direction}
        selectedRoute={selectedRoute}
        isMultiRoute={isMultiRoute}
      />

      <CopyModal
        copyModalShow={copyModalShow}
        setCopyModalShow={setCopyModalShow}
        setInstructions={setInstructions}
        setIsLoading={setIsLoading}
        direction={direction}
        copyOptions={copyOptions}
        manualRoutingType={manualRoutingType}
        instructionsSeparatedByDirection={instructionsSeparatedByDirection}
        vtItensOptions={vtItensOptions}
      />

      <DeleteAllByDirectionModal
        directionToBeRemoved={direction}
        deleteAllByDirectionModalShow={deleteAllByDirectionModalShow}
        setDeleteAllByDirectionModalShow={setDeleteAllByDirectionModalShow}
        setIsLoading={setIsLoading}
        setInstructions={setInstructions}
        selectedRoute={selectedRoute}
        isMultiRoute={isMultiRoute}
      />
    </>
  );
}
